import * as React from "react";
import {useMemo, useState} from "react";
import {IPlansTabResponse} from "../../../../api/AdminAnalyticsApi";
import {applySort, SortDir} from "../../../../utils/listUtil";
import {sortTh} from "../shared/SortableTh.component";
import {PORTFOLIO_PLANS_DESCRIPTION} from "../shared/analytics.constants";
import {numberWithCommas} from "../../../../utils/numberUtil";

interface IPortfolioPlansTableProps {
    rows: IPlansTabResponse["portfolioPlans"];
}

export const PortfolioPlansTable: React.FC<IPortfolioPlansTableProps> = ({rows}) => {
    const [portfolioSort, setPortfolioSort] = useState<{ col: string; dir: SortDir }>({
        col: "totalUsers",
        dir: "desc",
    });
    const sortedPortfolio = useMemo(
        () => applySort(rows, portfolioSort.col, portfolioSort.dir),
        [rows, portfolioSort]);

    return (
        <section className="admin-analytics__section">
            <div className="admin-analytics__section-header">
                <h2 className="admin-analytics__section-title">Portfolio Plan Engagement</h2>
                <span className="admin-analytics__section-subtitle">
                    Plans with Paris portfolio data or client portfolio features enabled
                </span>
            </div>
            <p className="admin-analytics__section-desc">{PORTFOLIO_PLANS_DESCRIPTION}</p>
            <div className="admin-analytics__summary-grid">
                <div className="admin-analytics__summary-card admin-analytics__summary-card--total">
                    <div className="admin-analytics__summary-label">Portfolio Plans</div>
                    <div className="admin-analytics__summary-value">
                        {rows.filter((r) => r.hasParisPlan).length}
                    </div>
                    <div className="admin-analytics__summary-note">plans with Paris portfolio data</div>
                </div>
                <div className="admin-analytics__summary-card admin-analytics__summary-card--internal">
                    <div className="admin-analytics__summary-label">Client Portfolio Enabled</div>
                    <div className="admin-analytics__summary-value">
                        {rows.filter((r) => r.clientPortfolioEnabled).length}
                    </div>
                    <div className="admin-analytics__summary-note">plans with client portfolio on</div>
                </div>
                <div className="admin-analytics__summary-card admin-analytics__summary-card--external">
                    <div className="admin-analytics__summary-label">Adoption Gaps</div>
                    <div className="admin-analytics__summary-value">
                        {rows.filter((r) => r.hasAdoptionGap).length}
                    </div>
                    <div className="admin-analytics__summary-note">portfolio enabled but no portfolio
                        page visits
                    </div>
                </div>
            </div>
            <table className="admin-analytics__table">
                <thead>
                <tr>
                    {sortTh("planName", "Plan", portfolioSort, setPortfolioSort, undefined, "asc")}
                    {sortTh("hasParisPlan", "Paris Data", portfolioSort, setPortfolioSort, undefined, "desc")}
                    {sortTh("clientPortfolioEnabled", "Client Portfolio", portfolioSort, setPortfolioSort,
                        undefined, "desc")}
                    {sortTh("clientModelPortfolioEnabled", "Model Portfolio", portfolioSort, setPortfolioSort,
                        undefined, "desc")}
                    {sortTh("totalUsers", "Users", portfolioSort, setPortfolioSort,
                        "admin-analytics__cell--right")}
                    {sortTh("totalPageViews", "Page Views", portfolioSort, setPortfolioSort,
                        "admin-analytics__cell--right")}
                    {sortTh("portfolioPageViews", "Portfolio Views", portfolioSort, setPortfolioSort,
                        "admin-analytics__cell--right")}
                    {sortTh("hasAdoptionGap", "Gap", portfolioSort, setPortfolioSort, undefined, "desc")}
                </tr>
                </thead>
                <tbody>{sortedPortfolio.map((row) => (
                    <tr key={row.planName}
                        className={row.hasAdoptionGap ? "admin-analytics__gap-row" : ""}>
                        <td>{row.planName}</td>
                        <td>{row.hasParisPlan ? "Yes" : "No"}</td>
                        <td>{row.clientPortfolioEnabled ? "Yes" : "No"}</td>
                        <td>{row.clientModelPortfolioEnabled ? "Yes" : "No"}</td>
                        <td className="admin-analytics__cell--right">{numberWithCommas(row.totalUsers)}</td>
                        <td className="admin-analytics__cell--right">{numberWithCommas(row.totalPageViews)}</td>
                        <td className="admin-analytics__cell--right">{numberWithCommas(row.portfolioPageViews)}</td>
                        <td>{row.hasAdoptionGap ? "⚠ Gap" : "—"}</td>
                    </tr>
                ))}</tbody>
            </table>
        </section>
    );
};
